/**
 * Teacher → My Subjects
 *
 * Lists every subject this teacher teaches with quick counts (classes,
 * lessons, assignments, exams) and a syllabus preview pane.  Read-only in
 * Phase 1 — the upload / publish / version-control flow lands in Phase 3.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, Layers, ClipboardList, FileCheck, Users, Loader2,
} from 'lucide-react';
import { portalApi } from './teacherApi';

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-1.5 text-xs text-slate-500">
    <Icon className="h-3.5 w-3.5" />
    <span>{label} <strong className="text-slate-700">{value ?? 0}</strong></span>
  </div>
);

export const TeacherSubjects = () => {
  const { data: subjects = [], isLoading } = useQuery({
    queryKey: ['teacher-subjects'],
    queryFn: () => portalApi('/teacher/subjects'),
  });
  const [selected, setSelected] = useState(null);
  const active = selected || subjects[0];

  if (isLoading)
    return <div className="p-6 text-center"><Loader2 className="h-5 w-5 animate-spin inline" /> Loading subjects…</div>;
  if (subjects.length === 0)
    return <div className="text-center text-slate-500 p-8 border border-dashed rounded">No subjects assigned yet — ask your school admin to add you to the timetable.</div>;

  return (
    <div className="space-y-3" data-testid="teacher-subjects-page">
      <h2 className="text-lg font-semibold">My Subjects</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* List */}
        <div className="space-y-2 lg:col-span-1">
          {subjects.map((s) => (
            <button key={s.id} onClick={() => setSelected(s)}
                    className={`w-full text-left p-3 rounded border transition ${
                      active?.id === s.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`teacher-subject-${s.id}`}>
              <div className="font-medium text-sm flex items-center gap-2">
                <BookOpen className="h-4 w-4 text-indigo-500" />
                {s.name}
                {s.code && <Badge variant="outline" className="text-[10px]">{s.code}</Badge>}
              </div>
              <div className="flex flex-wrap gap-3 mt-1.5">
                <Stat icon={Users} label="classes" value={s.class_count} />
                <Stat icon={Layers} label="lessons" value={s.lesson_count} />
                <Stat icon={ClipboardList} label="assigns" value={s.assignment_count} />
                <Stat icon={FileCheck} label="exams" value={s.exam_count} />
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <Card className="lg:col-span-2">
          <CardContent className="pt-4 space-y-3">
            {active ? (
              <>
                <div>
                  <h3 className="text-base font-semibold">{active.name}</h3>
                  <p className="text-xs text-slate-500">{active.code || '—'} · {active.grade_level || 'all grades'}</p>
                </div>
                {active.description && (
                  <div className="bg-slate-50 border rounded p-3 text-sm">{active.description}</div>
                )}
                <div>
                  <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-1">Syllabus / Learning outcomes</h4>
                  {active.syllabus ? (
                    <div className="text-sm whitespace-pre-wrap">{active.syllabus}</div>
                  ) : Array.isArray(active.learning_outcomes) && active.learning_outcomes.length ? (
                    <ul className="list-disc list-inside text-sm space-y-0.5">
                      {active.learning_outcomes.map((o, i) => <li key={i}>{o}</li>)}
                    </ul>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No syllabus attached yet.</p>
                  )}
                </div>
                <div className="text-xs text-slate-500 pt-2 border-t">
                  Upload &amp; publish flow ships in Phase 3 — use the Learning Materials tab to view existing resources.
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-400">Pick a subject on the left.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TeacherSubjects;
